// src/components/ui/HeroImageThemeOverride.client.tsx

'use client'

import { useEffect } from 'react'
import { useTheme } from 'next-themes'
import {
  HERO_CANDIDATE_WIDTH,
  HERO_SIZES,
  buildHeroSrcSet,
  heroBasePath,
} from '~/lib/hero-image-contract'

const HERO_IMAGE_SELECTOR = '[data-hero-image]'

/**
 * Syncs the hero `<picture>` when the user manually overrides the OS color
 * scheme via next-themes. The SSR `<picture>` `<source>`s pick the correct
 * variant from `prefers-color-scheme`; that media query still matches the
 * *system* preference, so when the resolved theme diverges we must retarget the
 * `<img>` at the opposite variant ourselves.
 *
 * Crucially, this re-applies the RESPONSIVE contract — `srcset` (1136w + 2432w)
 * and `sizes` — rather than pinning the raw 2432px master. Without the srcset a
 * DPR-1 manual-override user would download the full master and trip Lighthouse
 * `uses-responsive-images` (the exact regression this restores); with it, the
 * DPR-1 client still gets the 1136w candidate and retina the master. The `src`
 * fallback is the small candidate too, so even a (hypothetical) no-srcset client
 * never fetches the master at DPR-1.
 *
 * @returns {null} Renders nothing — effect-only enhancement.
 */
export function HeroImageThemeOverride(): null {
  const { resolvedTheme } = useTheme()

  useEffect(() => {
    if (!resolvedTheme) {
      return
    }

    const img = document.querySelector<HTMLImageElement>(HERO_IMAGE_SELECTOR)
    if (!img) {
      return
    }

    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const themeIsDark = resolvedTheme === 'dark'

    if (themeIsDark === systemPrefersDark) {
      return
    }

    const variant = themeIsDark ? 'dark' : 'light'
    const current = img.currentSrc ?? img.getAttribute('src') ?? ''
    if (!current.includes(`hero-${variant}`)) {
      // Remove the media-gated <source>s (they still resolve to the *system*
      // variant) and drive the <img> directly with the responsive contract for
      // the resolved theme.
      for (const source of img.parentElement?.querySelectorAll('source') ?? []) {
        source.remove()
      }
      img.srcset = buildHeroSrcSet(variant)
      img.sizes = HERO_SIZES
      img.src = `${heroBasePath(variant)}-${HERO_CANDIDATE_WIDTH}.webp`
    }
  }, [resolvedTheme])

  return null
}
